/**
 * Generic LRU (Least Recently Used) cache utilities.
 *
 * This module provides a tiny, typed LRU cache abstraction built on top of
 * JavaScript's Map. It supports:
 * - Fixed-size LRU eviction (oldest entry removed when capacity is exceeded)
 * - An optional onEvict callback for cleanup (e.g., revoke object URLs)
 * - A non-mutating `peek` to read without affecting recency
 * - A helper `stableKey` for generating deterministic string keys from objects
 *
 * Example: Basic usage
 * ```ts
 * import {createLruCache} from '@contentlab/utils/cache';
 *
 * const cache = createLruCache<number>({ maxEntries: 2 });
 * cache.set('a', 1);
 * cache.set('b', 2);
 * cache.get('a'); // 1 (now 'a' is most-recent)
 * cache.set('c', 3); // evicts 'b' because it's the least-recent
 * ```
 *
 * Example: onEvict cleanup
 * ```ts
 * const cache = createLruCache<{ url: string}>({
 *   maxEntries: 100,
 *   onEvict: (_key, value) => URL.revokeObjectURL(value.url),
 * });
 * ```
 *
 * Example: stableKey
 * ```ts
 * const key = stableKey({ b: 2, a: 1 });
 * // key is a deterministic JSON string with sorted keys
 * ```
 */

/**
 * Options for configuring an LRU cache instance.
 */
export interface LruCacheOptions<V> {
  /** Maximum number of entries to keep before evicting the least recently used. */
  maxEntries?: number;
  /** Callback invoked when an entry is evicted. Useful for cleanup. */
  onEvict?: (key: string, value: V) => void;
}

/**
 * A minimal LRU cache interface.
 */
export interface LruCache<V> {
  /** Get a value and mark it as most-recently used. */
  get: (key: string) => V | undefined;
  /** Read a value without affecting its recency. */
  peek: (key: string) => V | undefined;
  /** Insert or update a value and mark it as most-recently used. */
  set: (key: string, value: V) => void;
  /** Check if a key exists (does not affect recency). */
  has: (key: string) => boolean;
  /** Delete a key (return value intentionally ignored). */
  delete: (key: string) => void;
  /** Remove all entries from the cache. */
  clear: () => void;
  /** Current number of entries in the cache. */
  readonly size: number;
  /** Iterator over keys in LRU order (oldest first). */
  keys: () => IterableIterator<string>;
}

/**
 * Create a new LRU cache instance.
 *
 * - Uses Map insertion order to track recency. A successful `get` re-inserts
 *   the entry to the end (most-recent). When capacity is exceeded, the oldest
 *   key is removed.
 * - Use `peek` during iterations to avoid mutating recency inadvertently.
 */
export function createLruCache<V>(
  options: LruCacheOptions<V> = {}
): LruCache<V> {
  const {maxEntries = 100, onEvict} = options;
  const map = new Map<string, V>();

  // Evict the least-recently used (oldest insertion) when above capacity.
  const evictIfNeeded = () => {
    if (map.size <= maxEntries) return;
    const oldestKey = map.keys().next().value as string | undefined;
    if (oldestKey !== undefined) {
      const oldestValue = map.get(oldestKey)!;
      map.delete(oldestKey);
      onEvict?.(oldestKey, oldestValue);
    }
  };

  return {
    // Get and refresh to most-recent by re-inserting
    get(key) {
      const val = map.get(key);
      if (val !== undefined) {
        map.delete(key);
        map.set(key, val);
      }
      return val;
    },
    // Read without changing recency
    peek(key) {
      return map.get(key);
    },
    // Set and ensure capacity
    set(key, value) {
      if (map.has(key)) map.delete(key);
      map.set(key, value);
      evictIfNeeded();
    },
    // Delete and ignore return value for a void signature
    has: (key) => map.has(key),
    delete: (key) => map.delete(key),
    clear: () => map.clear(),
    get size() {
      return map.size;
    },
    keys: () => map.keys(),
  };
}

/**
 * Create a deterministic string key from an object by JSON-stringifying with
 * sorted keys. Useful when you need a stable cache key for arbitrary inputs.
 *
 * Example:
 * ```ts
 * const key = stableKey({ b: 2, a: 1 });
 * cache.set(key, value);
 * ```
 */
export const stableKey = (obj: unknown) => {
  try {
    const keys = Object.keys(obj as any).sort();
    return JSON.stringify(obj, keys);
  } catch {
    return JSON.stringify(obj);
  }
};
